import { Seo, breadcrumbLd } from "../lib/seo";
import { MaskedLine, FadeIn, Reveal } from "../components/Reveal";
import { Eyebrow, ArrowLink, LinkBtnGold } from "../components/atoms";
import { BrochureLink } from "../components/BrochureLink";

const STEPS = [
  {
    num: "01",
    name: "Understand",
    service: "SuburbIQ",
    fit: "Weighing up a suburb or market? General suburb-level questions about buying, selling or retaining.",
    link: "/suburbiq",
    linkLabel: "Get my SuburbIQ report",
  },
  {
    num: "02",
    name: "Decide",
    service: "Property & Portfolio Assessment",
    fit: "Need personalised advice about property you already own? Assess performance, fit with goals, holding costs, risk, alternatives and opportunity cost.",
    link: "/property-portfolio-assessment",
    linkLabel: "Explore the Assessment",
  },
];

export default function HowWeWork() {
  return (
    <>
      <Seo
        title="How We Work — Understand. Decide. Act. | 80 Metrics"
        description="Understand your position. Decide with evidence. Act only when it makes sense. SuburbIQ and the Assessment are optional — go directly to advocacy when you’re ready."
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "How We Work", path: "/how-we-work" }])]}
      />
      <section className="bg-charcoal text-ivory" data-testid="hww-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-28 pb-14 lg:pb-20">
          <FadeIn delay={0.1}>
            <Eyebrow dark>How we work</Eyebrow>
          </FadeIn>
          <h1 className="mt-8 font-serif font-medium text-4xl sm:text-5xl lg:text-7xl leading-[1.04] tracking-tight max-w-4xl">
            <MaskedLine delay={0.25}>Understand your position.</MaskedLine>
            <MaskedLine delay={0.37}>Decide with evidence.</MaskedLine>
            <MaskedLine delay={0.49}>
              <span className="italic text-gold">Act when it makes sense.</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="bg-white" data-testid="hww-flow">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-28">
          {STEPS.map((s) => (
            <div key={s.num}>
              <Reveal>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 py-12 lg:py-16 items-start">
                  <div className="lg:col-span-3">
                    <span className="font-serif text-6xl lg:text-7xl font-medium text-gold">{s.num}</span>
                    <h2 className="mt-3 text-xs font-semibold tracking-[0.3em] uppercase text-charcoal">{s.name}</h2>
                  </div>
                  <div className="lg:col-span-6">
                    <h3 className="font-serif text-3xl lg:text-4xl font-medium text-charcoal" data-testid={`hww-step-${s.num}`}>{s.service}</h3>
                    <p className="mt-4 text-sm lg:text-base text-charcoal/65 leading-relaxed">{s.fit}</p>
                  </div>
                  <div className="lg:col-span-3 lg:text-right">
                    <ArrowLink to={s.link} data-testid={`hww-link-${s.num}`}>{s.linkLabel}</ArrowLink>
                  </div>
                </div>
              </Reveal>
              <div className="flex justify-center" aria-hidden="true">
                <div className="h-10 w-px bg-charcoal/25" />
              </div>
            </div>
          ))}

          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 py-12 lg:py-16 items-start">
              <div className="lg:col-span-3">
                <span className="font-serif text-6xl lg:text-7xl font-medium text-gold">03</span>
                <h2 className="mt-3 text-xs font-semibold tracking-[0.3em] uppercase text-charcoal">Act</h2>
              </div>
              <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 gap-px bg-charcoal/15 border border-charcoal/15">
                <div className="bg-white p-8 lg:p-10" data-testid="hww-act-buy">
                  <span className="text-[11px] font-semibold tracking-[0.3em] uppercase text-golddark">Buy</span>
                  <h3 className="mt-3 font-serif text-2xl lg:text-3xl font-medium text-charcoal">Buyer Advocacy</h3>
                  <p className="mt-3 text-sm text-charcoal/65 leading-relaxed">
                    Ready to buy — or assessing a specific property to purchase? Market selection, property assessment, due diligence, price evidence, negotiation and representation.
                  </p>
                  <div className="mt-6">
                    <ArrowLink to="/buyer-advocacy" data-testid="hww-link-buyer">Buyer Advocacy</ArrowLink>
                  </div>
                </div>
                <div className="bg-white p-8 lg:p-10" data-testid="hww-act-sell">
                  <span className="text-[11px] font-semibold tracking-[0.3em] uppercase text-golddark">Sell</span>
                  <h3 className="mt-3 font-serif text-2xl lg:text-3xl font-medium text-charcoal">Vendor Advocacy</h3>
                  <p className="mt-3 text-sm text-charcoal/65 leading-relaxed">
                    Ready to sell? Timing, pricing, agent selection, campaign decisions and independent sale oversight.
                  </p>
                  <div className="mt-6">
                    <ArrowLink to="/vendor-advocacy" data-testid="hww-link-vendor">Vendor Advocacy</ArrowLink>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-6 border border-gold/50 bg-bone px-6 lg:px-10 py-8" data-testid="hww-optional-note">
              <p className="font-serif text-xl lg:text-2xl font-medium text-charcoal">
                SuburbIQ and the Assessment are optional.
              </p>
              <p className="mt-3 text-sm text-charcoal/70 leading-relaxed max-w-2xl">
                You can go directly to Buyer or Vendor Advocacy when you are ready to act. And if you’re unsure where to
                start — tell us what you’re trying to decide and we’ll point you to the right starting point, including
                telling you if you don’t need a paid service.
              </p>
              <div className="mt-6">
                <LinkBtnGold to="/contact" data-testid="hww-cta">Discuss my next move</LinkBtnGold>
              </div>
              <div className="mt-5">
                <BrochureLink />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
